import axios from 'axios';
import toast from 'react-hot-toast';
import { blockedModuleForRequest } from '../utils/permissions';
import { getSelectedCompany } from '../utils/selectedCompany';
import { getViewedCompany, RESELLER_ALL_TENANT_ID, getResellerCompanyFilter } from '../utils/resellerCompany';
import { getSuperAdminSingleCompany } from '../utils/superAdminCompanyScope';

// The Machines company switcher scopes ONLY machine-domain data to the picked
// company (via the impersonation header the backend validates against the login's
// allowed companies). Kept to this allow-list so nothing else the login sees changes.
const MACHINE_SCOPED_PREFIXES = [
  '/machines',
  '/machine-groups',
  '/machine-logs',
  '/rapier-machine-logs',
  '/beams',
  '/beam-logs',
  '/meter-entries',
  '/employees',
  '/qualities',
];

const isMachineScoped = (url?: string): boolean => {
  if (!url) return false;
  const path = url.replace(/^https?:\/\/[^/]+/, '').replace(/^\/api/, '');
  return MACHINE_SCOPED_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix + '/') || path.startsWith(prefix + '?'));
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:6002/api';

// Check if running in Electron
const isElectron = window.electronAPI ? true : false;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Read-only guard (backstop): block writes to modules the user can't write.
    const blocked = blockedModuleForRequest(config.method, config.url);
    if (blocked) {
      toast.error('You have read-only access to this section.');
      return Promise.reject(new axios.Cancel('read-only-access'));
    }
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Some requests (the SUPERADMIN switcher's own "every company" list fetch) must
    // never be scoped to whatever company is currently impersonated — scoping that
    // specific request would collapse the switcher's own dropdown down to just the one
    // company currently selected, making every other company unreachable without first
    // returning to "All Companies". Callers opt out via this marker header.
    if (config.headers?.['x-skip-impersonation']) {
      delete config.headers['x-skip-impersonation'];
      return config;
    }

    // A reseller viewing one of their companies: scope EVERY request to that company.
    // Takes priority over the machine-only switcher below since it's a full view, not
    // a single-domain one. The backend ignores this header unless the reseller created
    // that company, so it is safe to send.
    const viewedCompany = getViewedCompany();
    const superAdminCompany = getSuperAdminSingleCompany();
    if (viewedCompany && viewedCompany.tenantId === RESELLER_ALL_TENANT_ID) {
      // Reseller "all my companies" merged view. The backend derives the visible
      // tenant set from parentResellerId, so this header can't widen scope.
      config.headers['x-reseller-scope'] = 'all';
      const filter = getResellerCompanyFilter();
      if (filter != null) config.headers['x-reseller-filter-tenant-id'] = String(filter);
    } else if (viewedCompany) {
      config.headers['x-impersonate-tenant-id'] = String(viewedCompany.tenantId);
    } else if (superAdminCompany) {
      config.headers['x-impersonate-tenant-id'] = String(superAdminCompany.tenantId);
    } else {
      // Multi-company logins: scope machine-domain requests to the company picked in
      // the switcher. The backend ignores this header unless the company is in the
      // login's allow-list, so it is safe to send.
      const company = getSelectedCompany();
      if (company && isMachineScoped(config.url)) {
        config.headers['x-impersonate-tenant-id'] = String(company.tenantId);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // A wrong password on /auth/login is also a 401 — let the login form show it
    // instead of reloading the page (which wipes the error message).
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      // Token expired or invalid
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Enhanced API service that works with both online and offline modes
class APIService {
  private isOnline = true;

  constructor() {
    this.setupConnectionMonitoring();
  }

  private setupConnectionMonitoring() {
    if (isElectron) {
      // Listen for connection status changes from Electron
      window.addEventListener('connectionStatusChange', (event: any) => {
        this.isOnline = event.detail.isOnline;
        console.log('Connection status changed:', this.isOnline ? 'Online' : 'Offline');
      });
    } else {
      // Use browser's online/offline events
      window.addEventListener('online', () => {
        this.isOnline = true;
        console.log('Connection status: Online');
      });
      
      window.addEventListener('offline', () => {
        this.isOnline = false;
        console.log('Connection status: Offline');
      });
    }
  }

  async request(endpoint: string, options: any = {}) {
    if (isElectron && !this.isOnline) {
      // Use offline API
      return this.offlineRequest(endpoint, options);
    } else {
      // Use online API
      return this.onlineRequest(endpoint, options);
    }
  }

  private async onlineRequest(endpoint: string, options: any) {
    try {
      const response = await api.request({
        url: endpoint,
        ...options
      });
      return response.data;
    } catch (error: any) {
      // If online request fails and we're in Electron, try offline
      if (isElectron) {
        console.log('Online request failed, trying offline mode...');
        return this.offlineRequest(endpoint, options);
      }
      throw error;
    }
  }

  private async offlineRequest(endpoint: string, options: any) {
    if (!isElectron) {
      throw new Error('Offline mode not available in web version');
    }

    try {
      const result = await window.offlineAPI?.request(endpoint, options);
      return result;
    } catch (error) {
      console.error('Offline request failed:', error);
      throw error;
    }
  }

  // Convenience methods
  async get(endpoint: string, options: { headers?: Record<string, string> } = {}) {
    return this.request(endpoint, {
      method: 'GET',
      ...(options.headers && { headers: options.headers })
    });
  }

  async post(endpoint: string, data: any) {
    return this.request(endpoint, { 
      method: 'POST', 
      data: data 
    });
  }

  async put(endpoint: string, data: any) {
    return this.request(endpoint, { 
      method: 'PUT', 
      data: data 
    });
  }

  async delete(endpoint: string, options: { data?: any } = {}) {
    return this.request(endpoint, { 
      method: 'DELETE',
      ...(options.data && { data: options.data })
    });
  }

  // Multipart upload (e.g. a firmware .bin). Online-only: the Electron offline API
  // can't carry files. Auth token, write guard and 401 handling come from the axios
  // interceptors above. The 'multipart/form-data' header replaces the instance's
  // JSON default (which would make axios JSON-encode the FormData); axios then drops
  // it so the browser sets the real header with its boundary.
  async upload(endpoint: string, formData: FormData) {
    const response = await api.request({
      url: endpoint,
      method: 'POST',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Sync methods
  async syncData() {
    if (isElectron) {
      return window.offlineAPI?.syncData();
    }
    throw new Error('Sync not available in web version');
  }

  getConnectionStatus() {
    if (isElectron) {
      return window.offlineAPI?.getConnectionStatus() || false;
    }
    return navigator.onLine;
  }

  isElectronApp() {
    return isElectron;
  }
}

// Create and export instance
const apiService = new APIService();
export default apiService;

// Also export the original axios instance for backward compatibility
export { api };
